import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle } from 'lucide-react';

const Consulting = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const benefits = [
    'Comprehensive process mapping and analysis',
    'Proprietary automation-fit scoring methodology',
    'Technology-agnostic recommendations',
    'Implementation oversight and change management',
    'Ongoing optimisation and ROI tracking'
  ];

  const steps = [
    {
      number: '1',
      title: 'Discovery',
      description: 'Deep dive into your operations, systems, and pain points through structured interviews and process documentation.'
    },
    {
      number: '2',
      title: 'Analysis',
      description: 'Each process receives an automation-fit score based on complexity, frequency, error-proneness, and strategic value.'
    },
    {
      number: '3',
      title: 'Roadmap',
      description: 'Prioritised recommendations with clear ROI projections, risk assessment, and implementation timeline.'
    },
    {
      number: '4',
      title: 'Execution',
      description: 'Vendor-neutral implementation support, change management guidance, and performance tracking.'
    }
  ];

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const current = sectionRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (current) {
      observer.observe(current);
    }

    return () => {
      if (current) {
        observer.unobserve(current);
      }
    };
  }, []);

  return (
    <section id="consulting" ref={sectionRef} className="py-12 sm:py-16 md:py-24 bg-white relative overflow-hidden">
      {/* Floating Gradient Orbs - Reduced on mobile */}
      <div className="absolute top-1/4 left-1/4 w-64 h-64 sm:w-96 sm:h-96 bg-gradient-to-br from-slate-400/18 to-blue-400/18 rounded-full blur-3xl animate-float-orb-1"></div>
      <div className="absolute top-1/2 right-1/4 w-56 h-56 sm:w-80 sm:h-80 bg-gradient-to-br from-purple-400/15 to-slate-500/15 rounded-full blur-3xl animate-float-orb-3"></div>
      <div className="hidden md:block absolute bottom-1/4 left-1/3 w-72 h-72 bg-gradient-to-br from-blue-500/12 to-purple-500/12 rounded-full blur-3xl animate-float-orb-2"></div>
      <div className="hidden lg:block absolute top-1/3 right-1/2 w-84 h-84 bg-gradient-to-br from-purple-500/16 to-blue-400/16 rounded-full blur-3xl animate-float-orb-4"></div>
      <div className="absolute top-0 right-0 w-64 h-64 sm:w-96 sm:h-96 bg-slate-200/30 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 mb-12 sm:mb-20">
          <div className={`transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
          }`}>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4 sm:mb-6 leading-tight">
              Strategic consulting for <span className="italic text-slate-600">automation</span> that works
            </h2>
            <p className="text-base sm:text-lg text-slate-600 mb-6 sm:mb-8 leading-relaxed">
              Not everything should be automated. Our Auto-Mate practice helps organisations make intelligent decisions about where automation creates value—and where it creates risk.
            </p>
            <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
              {benefits.map((benefit, index) => (
                <li 
                  key={benefit} 
                  className={`flex items-start gap-3 transition-all duration-500 ${
                    isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                  }`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm sm:text-base text-slate-700">{benefit}</span>
                </li>
              ))}
            </ul>
            <button 
              className="w-full sm:w-auto bg-slate-900 text-white hover:bg-slate-800 text-sm sm:text-base px-6 sm:px-8 py-3 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
              onClick={scrollToContact}
            >
              Request Consultation
            </button>
          </div>

          <div className={`bg-gradient-to-br from-slate-50/80 to-slate-100/80 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-slate-200/50 shadow-xl transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
          }`}>
            <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-6 sm:mb-8">How we work</h3>
            <div className="space-y-4 sm:space-y-6">
              {steps.map((step, index) => (
                <div 
                  key={step.number} 
                  className={`flex gap-3 sm:gap-4 group hover:translate-x-2 transition-all duration-500 ${
                    isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
                  }`}
                  style={{ transitionDelay: `${index * 150}ms` }}
                >
                  <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-slate-800 to-slate-900 text-white flex items-center justify-center font-bold text-base sm:text-lg shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all">
                    {step.number}
                  </div>
                  <div>
                    <h4 className="text-base sm:text-lg font-bold text-slate-900 mb-1 sm:mb-2">{step.title}</h4>
                    <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Consulting;